import React from 'react';
import { ShadowRootWrapper } from '../ShadowRootWrapper';

interface MalachiteContextProps {
    children: React.ReactNode;
    className?: string;
    /**
     * Stretches the shadow host, and every plain div between it and `children`, to 100% of
     * the height available from this component's own parent. Without it, the host sizes to
     * its content: a percentage height set further down (inside `children`) has nothing
     * non-`auto` to resolve against, and a chain of plain `<div>`s does not propagate a
     * parent's height to its children on its own. Off by default: every existing consumer
     * relies on the host hugging its content, and turning this on for all of them would
     * change how much space they take up.
     */
    fillHeight?: boolean;
}

/**
 * MalachiteContext provides a Shadow DOM context with theme setup for Malachite components.
 * Use this to wrap groups of Malachite components instead of each component managing its own Shadow DOM.
 *
 * @example
 * <MalachiteContext>
 *   <Button variant="filled">Click me</Button>
 *   <Card>
 *     <CardContent>Hello world</CardContent>
 *   </Card>
 * </MalachiteContext>
 */
export const MalachiteContext: React.FC<MalachiteContextProps> = ({
    children,
    className = '',
    fillHeight = false,
}) => {
    return (
        <ShadowRootWrapper
            fillHeight={fillHeight}
            hostStyle={fillHeight ? { height: '100%' } : undefined}
        >
            <div className={className} style={fillHeight ? { height: '100%' } : undefined}>
                {children}
            </div>
        </ShadowRootWrapper>
    );
};

export default MalachiteContext;
